import { SxProps, Theme } from "@mui/material";
import { grey } from "@mui/material/colors";

const PRIMARY_DARK = "#014638";
const PRIMARY_DARKER = "#013124";
const YELLOW = "#F7EE6F";
const PURPLE_LIGHT = "#f2f3fe";
const PURPLE_DOT = "#8084FF";
const CREAM_CARD = "#f8f5ea";
const FOOTER_BG = "#fdfcf8";
const BORDER = "#e8e5d8";
const TEXT_BODY = "#151E16";
const TEXT_SECONDARY = "#5a6258";
const TEXT_MUTED = "#8d9189";
const SUCCESS = "#1F9D55";
const ERROR_DOT = "#C0392B";
const WHY_TEXT = "#f0eee2";
const WHY_SOURCE = "#a8a98f";

const styles: Record<string, SxProps<Theme>> = {
  container: {
    width: 360,
    maxWidth: "calc(100vw - 32px)",
    bgcolor: "#fff",
    borderRadius: "12px",
    border: `1px solid ${PURPLE_LIGHT}`,
    boxShadow: "0 1px 2px rgba(0,0,0,0.06), 0 12px 32px rgba(20, 30, 22, 0.18)",
    overflow: "hidden",
    fontFamily: "Inter, sans-serif",
  },
  head: {
    px: "14px",
    pt: "11px",
    pb: "11px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    bgcolor: PRIMARY_DARK,
    color: YELLOW,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    flexShrink: 0,
  },
  avatarIcon: { fontSize: 12 },
  helperBlock: { flex: 1, minWidth: 0 },
  helperText: {
    fontFamily: "InterTight, sans-serif",
    fontWeight: 600,
    fontSize: 13,
    color: TEXT_BODY,
    lineHeight: 1.1,
  },
  kindRow: {
    fontFamily: "InterTight, sans-serif",
    fontSize: 11,
    color: TEXT_SECONDARY,
    mt: "2px",
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
  },
  kindDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    display: "inline-block",
    bgcolor: PURPLE_DOT,
  },
  closeButton: {
    width: 26,
    height: 26,
    color: TEXT_MUTED,
    "&:hover": { bgcolor: CREAM_CARD, color: TEXT_BODY },
  },
  closeIcon: { fontSize: 13 },
  body: { px: "16px", pt: "4px", pb: "12px" },
  title: {
    fontFamily: "InterTight, sans-serif",
    fontWeight: 600,
    fontSize: 15,
    color: TEXT_BODY,
    lineHeight: 1.3,
    mb: "6px",
  },
  rationale: {
    fontSize: 12.5,
    lineHeight: 1.55,
    color: TEXT_SECONDARY,
    mb: "12px",
  },
  rawUrl: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    bgcolor: CREAM_CARD,
    borderRadius: "10px",
    px: "12px",
    py: "9px",
    mb: "12px",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 11.5,
    color: TEXT_SECONDARY,
    wordBreak: "break-all",
  },
  rawUrlIcon: { color: TEXT_MUTED, fontSize: 14, flexShrink: 0 },
  rawUrlText: { minWidth: 0 },
  fieldLabel: {
    fontFamily: "InterTight, sans-serif",
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: TEXT_SECONDARY,
    mb: "6px",
  },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    bgcolor: "#fff",
    border: `1px solid ${BORDER}`,
    borderRadius: "10px",
    px: "10px",
    py: "2px",
    "&:focus-within": {
      borderColor: PRIMARY_DARK,
      boxShadow: "0 0 0 3px rgba(1, 70, 56, 0.10)",
    },
  },
  inputIcon: { color: TEXT_MUTED, fontSize: 12 },
  input: {
    flex: 1,
    border: "none",
    outline: "none",
    bgcolor: "transparent",
    fontFamily: "Inter, sans-serif",
    fontSize: 12.5,
    color: TEXT_BODY,
    py: "8px",
    minWidth: 0,
    "&::selection": { bgcolor: "rgba(128, 132, 255, 0.35)" },
  },
  textareaWrapper: {
    bgcolor: "#fff",
    border: `1px solid ${BORDER}`,
    borderRadius: "10px",
    px: "10px",
    py: "8px",
    "&:focus-within": {
      borderColor: PRIMARY_DARK,
      boxShadow: "0 0 0 3px rgba(1, 70, 56, 0.10)",
    },
  },
  textarea: {
    width: "100%",
    display: "block",
    border: "none",
    outline: "none",
    resize: "vertical",
    bgcolor: "transparent",
    fontFamily: "Inter, sans-serif",
    fontSize: 12.5,
    lineHeight: 1.55,
    color: TEXT_BODY,
    minHeight: 72,
    "&::selection": { bgcolor: "rgba(128, 132, 255, 0.35)" },
  },
  preview: {
    mt: "10px",
    fontSize: 11.5,
    lineHeight: 1.5,
    color: TEXT_SECONDARY,
    fontStyle: "italic",
  },
  examplesCard: {
    bgcolor: CREAM_CARD,
    borderRadius: "10px",
    px: "14px",
    py: "10px",
  },
  examplesHead: {
    fontFamily: "InterTight, sans-serif",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: TEXT_SECONDARY,
    mb: "6px",
  },
  examplesList: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  exampleItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    fontSize: 12.5,
    lineHeight: 1.5,
    color: TEXT_BODY,
    textAlign: "left",
    width: "100%",
    px: "4px",
    py: "2px",
    borderRadius: "6px",
    cursor: "pointer",
    "&:hover": { bgcolor: "rgba(31, 157, 85, 0.08)" },
  },
  exampleArrow: {
    color: SUCCESS,
    fontWeight: 700,
    fontSize: 13,
    lineHeight: 1.5,
    flexShrink: 0,
  },
  exampleTryHint: {
    fontSize: 9,
    color: SUCCESS,
    ml: "6px",
  },
  kindDotError: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    display: "inline-block",
    bgcolor: ERROR_DOT,
  },
  diffCard: {
    bgcolor: CREAM_CARD,
    borderRadius: "10px",
    px: "12px",
    py: "10px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    fontSize: 12.5,
    lineHeight: 1.5,
  },
  diffRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
  },
  diffMarkerDel: {
    fontFamily: "InterTight, sans-serif",
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    borderRadius: "4px",
    px: "5px",
    py: "1px",
    flexShrink: 0,
    mt: "2px",
    lineHeight: "14px",
    bgcolor: "rgba(192, 57, 43, 0.12)",
    color: "#C0392B",
  },
  diffMarkerAdd: {
    fontFamily: "InterTight, sans-serif",
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    borderRadius: "4px",
    px: "5px",
    py: "1px",
    flexShrink: 0,
    mt: "2px",
    lineHeight: "14px",
    bgcolor: "rgba(0, 186, 19, 0.12)",
    color: "#008f0f",
  },
  diffTextDel: {
    flex: 1,
    minWidth: 0,
    color: TEXT_SECONDARY,
    textDecoration: "line-through",
    textDecorationColor: "rgba(192, 57, 43, 0.6)",
    textDecorationThickness: "1.5px",
  },
  diffTextAdd: {
    flex: 1,
    minWidth: 0,
    color: PRIMARY_DARK,
    fontWeight: 500,
  },
  whyBlock: {
    bgcolor: PRIMARY_DARK,
    color: WHY_TEXT,
    px: "16px",
    py: "12px",
    fontSize: 11.5,
    lineHeight: 1.55,
  },
  whyHead: {
    fontFamily: "InterTight, sans-serif",
    fontWeight: 600,
    fontSize: 10,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: YELLOW,
    mb: "4px",
  },
  whySource: {
    mt: "6px",
    fontSize: 10.5,
    color: WHY_SOURCE,
  },
  infoBox: {
    display: "flex",
    alignItems: "flex-start",
    gap: "9px",
    bgcolor: PURPLE_LIGHT,
    borderRadius: "10px",
    px: "12px",
    py: "10px",
    mt: "12px",
    fontSize: 12,
    lineHeight: 1.55,
    color: TEXT_SECONDARY,
  },
  infoBoxIcon: { color: PURPLE_DOT, fontSize: 13, flexShrink: 0, mt: "2px" },
  linkInfoCard: {
    bgcolor: CREAM_CARD,
    borderRadius: "10px",
    px: "14px",
    py: "11px",
    mb: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  linkInfoRow: {
    display: "flex",
    alignItems: "baseline",
    gap: "10px",
  },
  linkInfoLabel: {
    fontFamily: "InterTight, sans-serif",
    fontSize: 9.5,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: TEXT_MUTED,
    flexShrink: 0,
    width: 64,
  },
  linkInfoText: {
    flex: 1,
    minWidth: 0,
    fontSize: 12.5,
    color: TEXT_BODY,
    wordBreak: "break-word",
  },
  linkInfoUrlError: {
    flex: 1,
    minWidth: 0,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 11.5,
    color: ERROR_DOT,
    wordBreak: "break-all",
  },
  mergeTagToken: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 12,
    color: PURPLE_DOT,
    bgcolor: PURPLE_LIGHT,
    borderRadius: "6px",
    px: "8px",
    py: "3px",
    display: "inline-block",
  },
  previewLink: {
    color: "#776B5D",
    fontStyle: "normal",
    textUnderlineOffset: "2px",
    cursor: "pointer",
  },
  previewHtml: {
    fontStyle: "normal",
    color: TEXT_SECONDARY,
    "& a": {
      color: "#776B5D",
      textDecoration: "underline",
      textUnderlineOffset: "2px",
      cursor: "pointer",
    },
  },
  actions: {
    px: "14px",
    py: "12px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    borderTop: `1px solid ${PURPLE_LIGHT}`,
    bgcolor: FOOTER_BG,
  },
  primaryButton: {
    flex: 1,
    bgcolor: PRIMARY_DARK,
    color: "#fff",
    fontFamily: "InterTight, sans-serif",
    fontWeight: 600,
    fontSize: 13,
    px: "14px",
    py: "9px",
    borderRadius: "999px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "7px",
    letterSpacing: "0.02em",
    transition: "background 0.12s",
    "&:hover": { bgcolor: PRIMARY_DARKER },
  },
  primaryIcon: { fontSize: 13 },
  primaryButtonDisabled: {
    flex: 1,
    bgcolor: CREAM_CARD,
    color: TEXT_MUTED,
    fontFamily: "InterTight, sans-serif",
    fontWeight: 600,
    fontSize: 13,
    px: "14px",
    py: "9px",
    borderRadius: "999px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "7px",
    letterSpacing: "0.02em",
    cursor: "default",
    pointerEvents: "none",
  },
  pasteButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    flexShrink: 0,
    bgcolor: "#fff",
    border: `1px solid ${BORDER}`,
    borderRadius: "8px",
    color: TEXT_SECONDARY,
    fontFamily: "InterTight, sans-serif",
    fontSize: 11.5,
    fontWeight: 600,
    px: "9px",
    py: "5px",
    "&:hover": { bgcolor: CREAM_CARD, color: TEXT_BODY },
  },
  pasteButtonIcon: { fontSize: 11 },
  rejectButton: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    bgcolor: "#fff",
    border: `1px solid ${BORDER}`,
    color: TEXT_BODY,
    flexShrink: 0,
    "&:hover": {
      bgcolor: "#fef0ee",
      borderColor: "#C0392B",
      color: "#C0392B",
    },
  },
  rejectIcon: { fontSize: 14 },
  whyButton: {
    fontFamily: "InterTight, sans-serif",
    fontSize: 12,
    fontWeight: 500,
    color: TEXT_SECONDARY,
    px: "10px",
    py: "6px",
    borderRadius: "999px",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
    "&:hover": { bgcolor: grey[100], color: TEXT_BODY },
  },
  whyIcon: { fontSize: 12 },
};

export default styles;
